// Mirrors functions/src/utils/wheelWinnerAnnouncement.ts (kept in sync manually — this admin app
// and the functions app are separate TypeScript projects with no shared package).
import { WheelPrizeFormData, WinnerNotificationsFormData } from "./wheelPrizeTypes"

export const ANONYMOUS_LABEL = "A WinFacil user"
export const NOTIFICATION_TITLE = "La Ruleta Win"
const DEFAULT_TEMPLATE = "{name} just won {prize} on La Ruleta Win!"

export const applyTemplate = (template: string, vars: Record<string, string>): string =>
  Object.entries(vars).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

// A prize's own `announceWinner` (true/false) always overrides the global scope; `null` means
// "inherit," in which case the global `scope` decides. A non-winning ("try again") prize can
// never be announced, regardless of `announceWinner` — there's no winner to name.
export const isWinnerAnnouncementEligible = (prize: WheelPrizeFormData, config: WinnerNotificationsFormData): boolean => {
  if (!prize.winning) {
    return false
  }
  if (prize.announceWinner === false) {
    return false
  }
  if (prize.announceWinner === true) {
    return config.enabled !== false
  }
  if (!config.enabled || !config.scope || config.scope === "disabled") {
    return false
  }
  switch (config.scope) {
    case "all":
      return true
    case "grandPrizeOnly":
      return Boolean(prize.grandPrize)
    case "selectedTypes":
      return (config.selectedTypes ?? []).includes(prize.type)
    default:
      return false
  }
}

const formatPrizeLabel = (prize: WheelPrizeFormData): string => prize.shortLabel ?? prize.label

// Builds the push notification a real winner would receive for this prize, using the same
// eligibility rules and template substitution as the backend. Returns null when this prize
// wouldn't trigger an announcement under the current Winner-tab settings.
export const buildWinnerNotificationPreview = (
  prize: WheelPrizeFormData,
  config: WinnerNotificationsFormData,
): { title: string; body: string } | null => {
  if (!isWinnerAnnouncementEligible(prize, config)) {
    return null
  }
  const winnerName = config.privacyPolicy === "anonymous" ? ANONYMOUS_LABEL : "Maria R."
  const template = config.template?.trim() || DEFAULT_TEMPLATE
  const body = applyTemplate(template, {
    name: winnerName,
    prize: formatPrizeLabel(prize),
    time: new Date().toLocaleTimeString(),
  })
  return { title: NOTIFICATION_TITLE, body }
}
